import { Box, InputLabel, Stack } from "@mui/material";
import { Card, Checkbox, Divider, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { type GetShiftResponse } from "@src/appV2/Shifts/Shift/api/useGetShift";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import SignaturePad from "react-signature-pad-wrapper";

import { TextButton } from "../../../components/TextButton";
import { type CreateShiftVerificationRequestFormValues } from "../types";

interface CreateShiftVerificationRequestSignatureFormProps {
  workplaceTimezone: string;
  shiftGeofence: GetShiftResponse["response"]["geofence"];
}

export function CreateShiftVerificationRequestSignatureForm(
  props: CreateShiftVerificationRequestSignatureFormProps
) {
  const { workplaceTimezone, shiftGeofence } = props;

  const { control, resetField, setValue } =
    useFormContext<CreateShiftVerificationRequestFormValues>();

  const signaturePadRef = useRef<SignaturePad>(null);

  const onSignatureUpdate = useCallback(() => {
    const signature = signaturePadRef.current?.toDataURL();
    if (signature) {
      // Validation does not re-occur by default here, submit remians disabled otherwise
      setValue("signature", signature, { shouldValidate: true });
    }
  }, [setValue]);

  useEffect(() => {
    const signaturePad = signaturePadRef.current;

    signaturePad?.instance?.addEventListener("endStroke", onSignatureUpdate);
    return () => {
      signaturePad?.instance?.removeEventListener("endStroke", onSignatureUpdate);
    };
  }, [signaturePadRef, onSignatureUpdate]);

  return (
    <>
      <Stack spacing={4}>
        <Title semibold variant="h6" component="h6">
          Signature
        </Title>
        <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
          Please sign below
        </Text>
        <Card outlined sx={{ p: 5 }}>
          <Controller
            name="signature"
            control={control}
            render={() => <SignaturePad ref={signaturePadRef} redrawOnResize height={180} />}
          />
          <Divider sx={{ borderColor: (theme) => theme.palette.border?.primary, mb: 4 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <TextButton
              underline={false}
              onClick={() => {
                signaturePadRef.current?.clear();
                resetField("signature");
              }}
            >
              Clear
            </TextButton>
          </Box>
        </Card>
      </Stack>

      <InputLabel sx={{ whiteSpace: "normal" }}>
        <Box display="grid" gridTemplateColumns="min-content auto" alignItems="top" gap={2} pt={2}>
          <Controller
            name="acknowledgement"
            control={control}
            render={({ field }) => <Checkbox {...field} />}
          />
          <Text variant="body2">
            I acknowledge that Clipboard detected me entering the workplace geofence at{" "}
            {shiftGeofence?.entry
              ? formatInTimeZone(new Date(shiftGeofence.entry), workplaceTimezone, "MM/dd h:mm a")
              : "N/A"}{" "}
            and leaving at{" "}
            {shiftGeofence?.exit
              ? formatInTimeZone(new Date(shiftGeofence.exit), workplaceTimezone, "MM/dd h:mm a")
              : "N/A"}
            . This data will be shared with the workplace and there may be penalties if the
            workplace believes you submitted inaccurate clock in/out or break times.
          </Text>
        </Box>
      </InputLabel>
    </>
  );
}
